import { useCallback, useEffect, useState } from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import {
  Button, Card, Center, Container, Heading, Spinner, Stack, Text,
} from '@chakra-ui/react'
import { LuDownload } from 'react-icons/lu'
import { useAuth } from '../contexts/AuthContext'
import { CrowdsLogo } from '../components/CrowdsLogo'
import { useTranslation } from '../i18n'
import { fetchVideoExport } from '../services/videoExport'
import { clickDownloadLink } from '../services/videoDownload'

const ERROR_KEYS = {
  'functions/permission-denied': 'denied',
  'functions/failed-precondition': 'notReady',
  // Cloud Functions v2 scrubs any internal throw (e.g. a missing
  // `roles/iam.serviceAccountTokenCreator` binding, so `getVideoExport`
  // cannot mint a signed URL) down to this generic code. Without an entry
  // here it falls through to the `gone` copy, which tells every recipient
  // their download simply no longer exists — wrong, and worse to act on.
  'functions/internal': 'temporary',
}

/**
 * Which explanation this failure deserves.
 *
 * The code alone cannot separate an export that expired from one an admin
 * deleted — both are honestly `not-found` — so the callable puts the reason in
 * `details` and that wins when present.
 */
function errorKey(err) {
  if (err?.details?.reason === 'deleted') return 'deleted'
  return ERROR_KEYS[err?.code] || 'gone'
}

/**
 * Where every export email points.
 *
 * Public and restricted exports are the same page: the callable decides, and a
 * restricted one that nobody is signed in for asks for a sign-in rather than
 * reporting an error.
 *
 * A signed URL lives fifteen minutes, so the one fetched when the page loaded
 * is very likely stale by the time anyone clicks. Rather than lengthen the
 * URL, every download re-mints: the click calls the callable, takes the fresh
 * URL and saves from that. The page can sit open for a day and still work,
 * and because the callable re-runs the access check each time, revoking
 * access stops the very next download instead of waiting for links to lapse.
 */
export default function ExportDownloadPage() {
  const { exportId } = useParams()
  const location = useLocation()
  const { user } = useAuth()
  const { t, lang } = useTranslation()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await fetchVideoExport(exportId))
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [exportId])

  // The URL held in `data` is only evidence that a part exists; the one
  // actually used is fetched here, at the moment of the click, so it is always
  // minutes old rather than however long the page has been open. A failure
  // surfaces the same way a failed page load does, so a revoked or deleted
  // export says so instead of starting a download that 404s.
  const download = useCallback(async (name) => {
    setDownloading(name)
    try {
      const fresh = await fetchVideoExport(exportId)
      const part = fresh.parts.find((p) => p.name === name)
      if (!part) throw Object.assign(new Error('gone'), { code: 'gone' })
      setData(fresh)
      clickDownloadLink(part.url)
    } catch (err) {
      setError(err)
    } finally {
      setDownloading(null)
    }
  }, [exportId])

  // AuthProvider renders nothing until it has resolved the session, so by the
  // time this runs `user` is settled; re-running on it covers a sign-in that
  // happened on the way here.
  useEffect(() => { load() }, [user, load])

  if (loading) return <Center py={20}><Spinner /></Center>

  // A restricted export with nobody signed in is a sign-in prompt, not a
  // failure: the recipient followed a link from their own mail. `from` is what
  // LoginPage navigates back to, so they land on the export rather than on the
  // dashboard — rendering <LoginPage /> in place would lose that and bounce
  // them to `/`.
  if (error?.code === 'functions/unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (error) {
    return (
      <Container maxW="container.sm" py={16}>
        <Stack gap={4} align="center">
          <CrowdsLogo />
          <Heading size="md">{t('exportPage.unavailable')}</Heading>
          <Text color="fg.muted">{t(`exportPage.error.${errorKey(error)}`)}</Text>
        </Stack>
      </Container>
    )
  }

  const expires = data.expiresAt
    ? new Date(data.expiresAt).toLocaleDateString(lang === 'es' ? 'es-CL' : 'en-GB',
      { day: 'numeric', month: 'long', year: 'numeric' })
    : null

  return (
    <Container maxW="container.sm" py={16}>
      <Stack gap={6}>
        <CrowdsLogo />
        <Stack gap={1}>
          <Heading size="lg">{data.eventName}</Heading>
          <Text color="fg.muted">
            {t('exportPage.count', { count: data.videoCount })}
            {data.failedCount > 0 && ` · ${t('exportPage.failed', { count: data.failedCount })}`}
          </Text>
        </Stack>

        <Stack gap={3}>
          {data.parts.map((part) => (
            <Card.Root key={part.name}>
              <Card.Body>
                <Stack direction="row" justify="space-between" align="center">
                  <Stack gap={0}>
                    <Text fontWeight="medium">{part.name}</Text>
                    <Text fontSize="sm" color="fg.muted">
                      {t('exportPage.partSize', {
                        count: part.count,
                        size: (part.bytes / (1024 * 1024)).toFixed(0),
                      })}
                    </Text>
                  </Stack>
                  <Button
                    size="sm"
                    colorPalette="brand"
                    loading={downloading === part.name}
                    onClick={() => download(part.name)}
                  >
                    <LuDownload /> {t('exportPage.download')}
                  </Button>
                </Stack>
              </Card.Body>
            </Card.Root>
          ))}
        </Stack>

        {expires && <Text fontSize="sm" color="fg.muted">{t('exportPage.expires', { date: expires })}</Text>}
      </Stack>
    </Container>
  )
}
